import React, { useState, useEffect, type ComponentType } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/hooks/UseLanguage";
import ShopifyModal from "./Modals/ShopifyModal";
import FacebookModal from "./Modals/FacebookModal";
import GoogleAdsModal from "./Modals/GoogleAdsModal";
import GoogleAnalyticsModal from "./Modals/GoogleAnalyticsModal";
import InstagramModal from "./Modals/InstagramModal";
import MetaModal from "./Modals/MetaModal";
import { useConnectedSources } from "./Hooks/UseConnectedSources";
import { useSourceTokenSession } from "./Hooks/UseSourceTokenSession";
import type { ConnectorModalName, ConnectorModalProps } from "./Types/SourceTypes";
import {
  ONBOARDING_SOCIAL_MEDIA_ORDER,
  ONBOARDING_SOURCE_ID_TO_NAME_MAP,
} from "./Utils/SourceConstants";

interface OnboardingConnectSourcesProps {
  onConnectionCreated?: (_connectorName: string) => void;
  onNext?: (_isValid: boolean) => void;
  onBack?: () => void;
}

const CONNECTOR_MODALS: Record<ConnectorModalName, ComponentType<ConnectorModalProps>> = {
  Shopify: ShopifyModal,
  Facebook: FacebookModal,
  GoogleAds: GoogleAdsModal,
  GoogleAnalytics: GoogleAnalyticsModal,
  Instagram: InstagramModal,
  Meta: MetaModal,
};

const SOCIAL_MEDIA_IMAGES: Record<string, string> = {
  Shopify: "/images/shopify.png",
  GoogleAds: "/images/google.png",
  GoogleAnalytics: "/images/googleanalytics.png",
  Meta: "/images/meta.png",
  Facebook: "/images/facebook.png",
  Instagram: "/images/instagram.png",
  Snapchat: "/images/snapchat.png",
  Finish: "/images/finish.png",
};

const isConnectorModalName = (name: string): name is ConnectorModalName => name in CONNECTOR_MODALS;

const OnboardingConnectSources: React.FC<OnboardingConnectSourcesProps> = ({
  onConnectionCreated,
}) => {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token");
  const { t } = useTranslation();
  useLanguage();
  useSourceTokenSession(token);

  useEffect(() => {
    document.body.style.overflow = "hidden";
  }, []);

  const [activeModal, setActiveModal] = useState<ConnectorModalName | null>(null);
  const { connectedSources: connectedConnectors, setConnectedSources: setConnectedConnectors } =
    useConnectedSources(ONBOARDING_SOURCE_ID_TO_NAME_MAP, true);

  const roleID = localStorage.getItem("role_id");

  const handleConnectorConnected = (connectorName: string) => {
    Object.values(ONBOARDING_SOURCE_ID_TO_NAME_MAP).forEach(sourceName => {
      localStorage.setItem(`${sourceName}_connected`, (sourceName === connectorName).toString());
    });
    if (!connectedConnectors.includes(connectorName)) {
      setConnectedConnectors(prev => [...prev, connectorName]);
      onConnectionCreated?.(connectorName);
    }
  };

  const handleImageClick = (socialMedia: string) => {
    if (roleID === "1" && isConnectorModalName(socialMedia)) {
      setActiveModal(socialMedia);
    }
  };

  const ActiveModal = activeModal ? CONNECTOR_MODALS[activeModal] : null;

  return (
    <>
      <div className="source-connector-header">
        <div>
          <p className="h2design">{t("sourceConnector.title")}</p>
          <p className="finishline">{t("sourceConnector.subtitle")}</p>
        </div>
      </div>
      <div className="icon-grid zigzag-icons-container">
        {ONBOARDING_SOCIAL_MEDIA_ORDER.map((socialMedia, index) => (
          <div
            key={index}
            className={`icon frequency-icon ${socialMedia.toLowerCase()}-icon ${
              connectedConnectors.includes(socialMedia) ? "highlight" : ""
            }`}
            style={{
              marginLeft: index % 2 === 0 ? "20px" : "100px",
            }}
          >
            <img
              src={SOCIAL_MEDIA_IMAGES[socialMedia]}
              alt={socialMedia}
              onClick={() => handleImageClick(socialMedia)}
              className={`social-media-image ellipse ${socialMedia}`}
            />
          </div>
        ))}
      </div>

      {ActiveModal && activeModal && (
        <ActiveModal
          isOpen
          onClose={() => setActiveModal(null)}
          onConnected={() => {
            handleConnectorConnected(activeModal);
            setActiveModal(null);
          }}
        />
      )}
    </>
  );
};

export default OnboardingConnectSources;
